import React, { useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Alert from '@material-ui/lab/Alert';
import { useWindowWidth } from '@react-hook/window-size';
import Button from 'components/StyledButton/StyledButton';
import sanitizeHtml from 'sanitize-html';

import CustomGreenButton from '../../common/styled/CustomGreenButton/CustomGreenButton';
import CustomLoader from '../../components/CustomLoader/CustomLoader';
import { ReduxState } from '../../redux/types';
import Company, { EmptyCompany } from '../../types/Company';
import PlasticAssessment from '../../types/PlasticAssessment';
import api from '../../utils/api';
import { dateToLocale } from '../../utils/date';
import { useDialog } from '../../utils/dialog';
import snackbar from '../../utils/snackbar';
import { ActionTableCell, FlexContainer, Title } from './styled';

const PlasticAssessmentsPage = () => {
    const { companyId } = useParams<{ companyId: string }>();

    const userInfo = useSelector((state: ReduxState) => state.user);
    const realCompanyId = companyId || userInfo.companyId;

    const { t } = useTranslation();
    const history = useHistory();

    const [openDialog, closeDialog] = useDialog();

    const isCustomer = useSelector((state: ReduxState) => state.user.type) === 'customer';

    const windowWidth = useWindowWidth();
    const titleSuffix = windowWidth < 900 ? '_small' : '';

    const [isLoading, setIsLoading] = useState(true);
    const [assessments, setAssessments] = useState<PlasticAssessment[]>([]);

    const [companyData, setCompanyData] = useState<Company>(EmptyCompany);

    const assessmentBaseUrl = isCustomer ? '/plastic-assessments' : '/companies/' + realCompanyId + '/plastic-assessments';

    const getAssessmentsData = useCallback(() => {
        api.request('/companies/' + realCompanyId + '/plastic-assessments', 'GET').then((res) => {
            setAssessments(res);
            setIsLoading(false);
        });
    }, [realCompanyId]);

    const getData = useCallback(() => {
        api.request('/companies/' + realCompanyId).then((companyRes) => {
            setCompanyData(companyRes);
            getAssessmentsData();
        });
    }, [getAssessmentsData, realCompanyId]);

    const createAssessment = useCallback(() => {
        api.request('/companies/' + realCompanyId + '/plastic-assessments', 'POST').then((res) => {
            const { id } = res;
            snackbar.success(t('pages.plastic_assessments.new_assessment_saved'));
            history.push(assessmentBaseUrl + '/' + id);
        });
    }, [assessmentBaseUrl, history, realCompanyId, t]);

    const duplicateAssessment = useCallback((assessmentId: number) => {
        api.request('/companies/' + realCompanyId + '/plastic-assessments/' + assessmentId + '/duplicate', 'POST').then((res) => {
            const { id } = res;
            snackbar.success(t('pages.plastic_assessments.assessment_duplicated'));
            history.push(assessmentBaseUrl + '/' + id);
        });
    }, [assessmentBaseUrl, history, realCompanyId, t]);

    const deleteAssessment = useCallback((assessmentId: number) => {
        setIsLoading(true);
        api.request('/companies/' + realCompanyId + '/plastic-assessments/' + assessmentId, 'DELETE').then(() => {
            snackbar.success(t('pages.plastic_assessments.delete_confirm'));
            getData();
        }).catch(() => {
            setIsLoading(false);
        });
    }, [realCompanyId, getData, setIsLoading, t]);

    const deleteAssessmentConfirm = useCallback((assessmentId: number) => {
        openDialog({
            title: t('pages.plastic_assessments.delete_title'),
            message: t('pages.plastic_assessments.delete_message'),
            actions: [
                {
                    text: t('dialogs.generic.cancel'),
                    color: 'secondary',
                    action: closeDialog
                },
                {
                    text: t('dialogs.generic.confirm'),
                    color: 'primary',
                    action: () => {
                        deleteAssessment(assessmentId);
                        closeDialog();
                    }
                }
            ]
        });
    }, [closeDialog, deleteAssessment, openDialog, t]);

    useEffect(() => {
        getData();
    }, [getData]);

    if (isLoading) {
        return <CustomLoader />;
    }

    return (
        <>
            <FlexContainer>
                <Title variant='h4'>
                    {companyId ? (
                        <Trans i18nKey={'pages.plastic_assessments.title_with_company' + titleSuffix} tOptions={{ company: companyData.name }}>
                            _<small>{companyData.name}</small>_
                        </Trans>
                    ) : t('pages.plastic_assessments.title' + titleSuffix)}
                </Title>

                {isCustomer && <CustomGreenButton variant='outlined' onClick={() => createAssessment()}>
                    {t('pages.plastic_assessments.new_assessment')}
                </CustomGreenButton>}
            </FlexContainer>

            <TableContainer component={Paper} style={{ marginTop: '20px' }} className='book-list-table-view'>
                <Table aria-label='simple table'>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('pages.plastic_assessments.table.start_certification_date')}</TableCell>
                            <TableCell>{t('pages.plastic_assessments.table.creation_date')}</TableCell>
                            <TableCell>{t('pages.plastic_assessments.table.modification_date')}</TableCell>
                            <TableCell>{t('pages.plastic_assessments.table.monthly_plastic')}</TableCell>
                            <TableCell>{t('pages.plastic_assessments.table.monthly_co2')}</TableCell>
                            <TableCell>{t('pages.plastic_assessments.table.certified_co2_save')}</TableCell>
                            <TableCell>{t('pages.plastic_assessments.table.state')}</TableCell>
                            <ActionTableCell>{t('pages.generic.table.actions')}</ActionTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {assessments.map((assessment, i) => {
                            return (
                                <TableRow key={assessment.id}>
                                    <TableCell>{dateToLocale(assessment.startCertificationDate, false)}</TableCell>
                                    <TableCell>{dateToLocale(assessment.creationDate, false)}</TableCell>
                                    <TableCell>{dateToLocale(assessment.modificationDate, false)}</TableCell>
                                    <TableCell>{assessment.statsData.kgPlastic.toFixed(2)}</TableCell>
                                    <TableCell>{assessment.statsData.kgCo2.toFixed(2)}</TableCell>
                                    <TableCell>{((assessment.savedCo2 ?? 0) * -1).toFixed(2)}</TableCell>
                                    <TableCell>{t('models.plastic_assessment.enum.state.' + assessment.state)}</TableCell>
                                    <ActionTableCell>
                                        <Button variant='contained' color='primary' onClick={() => history.push(assessmentBaseUrl + '/' + assessment.id)}>
                                            {t('pages.generic.table.details')}
                                        </Button>{' '}

                                        {isCustomer && assessment.state === 'certified' && i === (assessments.length - 1) && (
                                            <>
                                                <Button variant='outlined' color='primary' onClick={() => duplicateAssessment(assessment.id)}>
                                                    {t('pages.plastic_assessments.table.duplicate')}
                                                </Button>{' '}
                                            </>
                                        )}

                                        {isCustomer && assessment.state === 'editing' && (
                                            <Button variant='outlined' color='secondary' onClick={() => deleteAssessmentConfirm(assessment.id)}>
                                                {t('pages.generic.table.delete')}
                                            </Button>
                                        )}
                                    </ActionTableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <Alert style={{ maxWidth: '1200px', margin: '26px auto 0' }} severity='info'>
                <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(t('pages.plastic_assessments.info_message')) }} />
            </Alert>
        </>
    );
};

export default PlasticAssessmentsPage;
